import classNames from 'classnames'
import { useState, useEffect, useCallback, useMemo } from 'react'
import useScrollBelow from 'src/hooks/useScrollBelow'
import useLastScrollDirection from 'src/hooks/useLastScrollDirection'
import { useCurrentUrl } from 'src/hooks/useCurrentUrl'
import NextLink from 'next/link'
import Img from 'src/components/shared/Img'
import { useAnalytics } from 'src/common/analytics'
import Subnav from './components/subnav'
import { Sidebar } from './components/Sidebar'
import StickyRender from './components/Sticky'
import useScrollThreshold from 'src/hooks/useScrollThreshold'
import { useClientDashboardLoginOverlayTrigger } from './../useClientDashboardLoginOverlayTrigger'
import {
  ActiveNavProvider,
  useActiveNavValue,
} from './context/ActiveNavContext'
import { CONTENT_TYPES, CONTENT_TYPES_MULTISITE } from 'src/common/constants'
import { useAnyElementInViewport } from 'src/common/hooks/useAnyElementInViewport'
import useScrollPosition from 'src/common/hooks/useScrollPosition'

export const rewriteAssetUrl = (url) => {
  return url
}

export const transformExternalContent = (content) => {
  if (!content) return undefined

  if (
    content.contentType === CONTENT_TYPES.ARTICLE_PAGES ||
    content.contentType === CONTENT_TYPES.BLOG_PAGES
  ) {
    return {
      cardTitleText: content.title,
      cardText: content.metaDescription,
      cardImage: rewriteAssetUrl(content.image || content.imageAsset?.url),
    }
  }

  return undefined
}

export const transformLongFormContent = (content) => {
  if (!content) return undefined

  return {
    cardTitleText: content.fields?.title,
    cardText: content.fields?.hook,
    cardImage: rewriteAssetUrl(
      content.fields?.mainImage?.fields?.mediaContent?.fields?.file?.url
    ),
  }
}

export const transformArticleCtaCard = (content) => {
  if (!content) return undefined

  return {
    cardTitleText: content.fields?.title?.content[0]?.content[0]?.value,
    cardText: content.fields?.subtitle?.content[0]?.content[0]?.value,
    cardImage: rewriteAssetUrl(
      content.fields?.image?.fields?.mediaContent?.fields?.file?.url
    ),
  }
}

export const transformArticleCardContent = (fields) => {
  if (fields.externalContent?.content) {
    return transformExternalContent(fields.externalContent.content)
  }

  const contentTypeId = fields.content?.sys?.contentType?.sys?.id

  if (contentTypeId === CONTENT_TYPES_MULTISITE.LONG_FORM_CONTENT) {
    return transformLongFormContent(fields.content)
  }

  if (contentTypeId === CONTENT_TYPES_MULTISITE.ARTICLE_CTA_CARD) {
    return transformArticleCtaCard(fields.content)
  }

  return undefined
}

export const Logo = () => {
  const { track } = useAnalytics()

  const handleLogoClick = useCallback(() => {
    const data = {
      nav_link_section: 'Mega Menu',
      click_type: 'Logo Click',
      click_id: 'Freedom Debt Relief',
      click_url: '/',
      click_text: 'FDR-Web | Freedom Debt Relief Logo',
      track_event: 'mega_menu',
      event_type: 'track',
    }

    track(data, {}, 'click')
  }, [track])

  return (
    <NextLink
      href="/"
      prefetch={false}
      shallow={true}
      data-testid="logo-link"
      onClick={handleLogoClick}
    >
      <div>
        <Img
          priority={true}
          dataTestId="fdr-logo"
          src="/next-assets/icons/fdr-logo.svg"
          alt="Freedom Debt Relief"
          className="h-10 w-20 lg:h-12 lg:w-28"
          layout="responsive"
        />
      </div>
    </NextLink>
  )
}

export const transformContentfulMegamenu = (
  contentfulData,
  currentURL = null
) => {
  if (!contentfulData) return {}

  const visitedIds = new Set()
  const url = currentURL ?? ''
  const toList = (value) =>
    Array.isArray(value)
      ? value.filter(Boolean).map(String)
      : value
      ? [String(value)]
      : []
  const isVisible = (fields) => {
    if (!currentURL) return true
    const whitelist = toList(fields.whitelistUrl)
    const blacklist = toList(fields.blacklistUrl)
    if (whitelist.length > 0 && !whitelist.some((value) => value === url))
      return false
    if (blacklist.length > 0 && blacklist.some((value) => value === url))
      return false

    return true
  }

  // Transform navigation item recursively
  const transformNav = (item) => {
    if (!item?.fields) return null

    const entryId = item.sys?.id
    if (entryId && visitedIds.has(entryId)) return null // Skip circular reference
    if (entryId) visitedIds.add(entryId)

    const { fields } = item
    if (!fields.variant) return null
    if (!isVisible(fields)) return null

    const transformed = {
      variant: fields.variant,
      id:
        entryId ||
        (fields.label
          ? fields.label
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^a-z0-9-]/g, '')
          : undefined),
      ...(fields.label !== undefined && { label: fields.label }),
      ...(fields.link && { link: fields.link }),
      ...(fields.description && { description: fields.description }),
      ...(fields.icon && {
        icon: rewriteAssetUrl(fields.icon?.fields?.file?.url),
      }),
      ...(fields.viewAllLabel && { viewAllLabel: fields.viewAllLabel }),
      ...(fields.columns != null && { columns: fields.columns }),
      ...(fields.variant === 'articleCard' &&
        (fields.externalContent?.content || fields.content) && {
          content: transformArticleCardContent(fields),
        }),
    }

    // Transform children
    if (fields.children?.length) {
      const children = fields.children
        .filter(
          (child) =>
            typeof child === 'object' &&
            child !== null &&
            !String(child).includes('[Circular')
        )
        .map((child) => transformNav(child))
        .filter(Boolean)
      if (!children.length) return null
      transformed.children = children
    }

    if (entryId) visitedIds.delete(entryId)

    return transformed
  }

  // Extract logo
  const logoUrl = contentfulData.logo?.fields?.mediaContent?.fields?.file?.url
  const partnerLogo = logoUrl
    ? logoUrl.startsWith('//')
      ? `https:${logoUrl}`
      : logoUrl
    : '/fdr-logo.svg'
  const partnerLogoAlt =
    contentfulData.logo?.fields?.mediaContent?.fields?.title ||
    contentfulData.logo?.fields?.name ||
    'FDR Logo'

  // Transform CTAs
  const transformCta = (cta) => ({
    label: cta.fields?.label || '',
    link: cta.fields?.link || '',
    style: cta.fields?.style || 'primary',
    show: cta.fields?.showOn || 'web',
    section: cta.fields?.section || 'ButtonsHeader',
    linkText: cta.fields?.linkText || '',
    linkType: cta.fields?.linkType || '',
    icon: cta.fields?.icon
      ? rewriteAssetUrl(cta.fields?.icon?.fields?.file?.url)
      : null,
  })

  return {
    partnerLogo,
    partnerLogoAlt,
    topCtas:
      contentfulData.topCtas
        ?.filter((cta) => cta?.fields && isVisible(cta.fields))
        .map(transformCta)
        .filter((cta) => cta.section === 'ButtonsHeader') || [],
    sidebarCtas:
      contentfulData.sidebarCtas
        ?.filter((cta) => cta?.fields && isVisible(cta.fields))
        .map(transformCta)
        .filter((cta) => cta.section === 'ButtonsHeader') || [],
    aboveHeaderCtas:
      contentfulData.topCtas
        ?.map(transformCta)
        .filter((cta) => cta.section === 'ButtonsAboveHeader') || [],
    navigation:
      contentfulData.navigation?.map(transformNav).filter(Boolean) || [],
  }
}

const SidebarButtons = ({
  sidebarCtas,
  isClientDashboardLoginLink,
  handleCtaClick,
}) => {
  return (
    <div className="items-justify flex w-full flex-col items-center justify-center gap-2 text-center lg:w-auto">
      {sidebarCtas.map((cta) => {
        const isLoginCta = isClientDashboardLoginLink(cta.link)

        return (
          <a
            key={cta.label}
            href={isLoginCta ? '#' : cta.link}
            rel={isLoginCta ? undefined : 'noopener'}
            target={isLoginCta ? undefined : '_blank'}
            onClick={(e) => handleCtaClick(e, cta)}
            className={classNames(
              `flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-13 font-bold`,
              {
                'border-2 border-solid border-blue-base bg-blue-base text-white':
                  cta.style === 'primary',
                'border-2 border-solid border-blue-135 bg-blue-135 text-blue-base':
                  cta.style === 'secondary',
                'border-2 border-solid border-blue-base bg-white text-blue-base':
                  cta.style === 'outline',
                'border-2 border-solid border-[#cb000e] bg-[#cb000e] text-white':
                  cta.style === 'red-primary',
                'border-2 bg-white text-blue-base': cta.style === 'text',
              }
            )}
          >
            {cta.icon && (
              <Img src={cta.icon} alt={cta.label} className="size-5" />
            )}
            {cta.label}
          </a>
        )
      })}
    </div>
  )
}

const NavItemLabel = ({ label, isHighlighted, isCurrentPage }) => {
  return (
    <div
      className="flex flex-col items-center gap-[10px] pt-2"
      data-active={isCurrentPage || undefined}
    >
      <span
        className={classNames(
          'font-medium! block whitespace-nowrap text-heading-4 transition-colors duration-200',
          isHighlighted ? 'text-blue-base' : 'text-black'
        )}
      >
        {label}
      </span>
      <span
        aria-hidden="true"
        className={classNames(
          'block h-1 w-full rounded-t-[2px] transition-colors duration-200',
          {
            'bg-blue-base': isHighlighted,
            'bg-transparent': !isHighlighted,
          }
        )}
      />
    </div>
  )
}

const Megamenu = ({ megamenuData }) => {
  const currentURL = useCurrentUrl()
  const menuData = useMemo(
    () => transformContentfulMegamenu(megamenuData, currentURL),
    [megamenuData, currentURL]
  )
  const partnerLogo = menuData?.partnerLogo
  const partnerLogoAlt = menuData?.partnerLogoAlt
  const topCtas = menuData?.topCtas || []
  const navigation = menuData?.navigation || []
  const aboveHeaderCtas = menuData?.aboveHeaderCtas || []
  const sidebarCtas = menuData?.sidebarCtas || []
  const mainCTA = topCtas.find((cta) => cta?.linkType === 'MainCTA')
  const activeNav = useActiveNavValue(navigation, currentURL)

  const isBelowStickyThreshold = useScrollBelow(50)
  const lastScrollDirection = useLastScrollDirection()
  const lastScrollDirectionZero = useLastScrollDirection(0)
  const isScrolling = useScrollThreshold(100)

  const [hoverNavigation, setHoverNavigation] = useState(false)
  const [hoverSubnav, setHoverSubnav] = useState(false)
  const [openSubnav, setOpenSubnav] = useState(false)
  const [openSidebar, setOpenSidebar] = useState(false)
  const [dataSubnav, setDataSubnav] = useState(null)
  const [hoveredNavId, setHoveredNavId] = useState(null)
  const scrollPosition = useScrollPosition()

  const ctasAreInViewport = useAnyElementInViewport(
    [
      'hero-banner-content-cta',
      'heroPrimaryBtnContent',
      'apply-url',
      'title-image',
      'hero-consumer-concept-cta',
      'hero-slider-cta',
      'Continue_Lead_CTA',
      'link-button_PromotingFinancialHealthAndAdvocacy',
      'keyTakeaways',
      'table-of-contents',
      'learn-how-it-works-facts-page-cta',
      'link-heroPrimaryButton',
    ],
    0
  )

  const { track } = useAnalytics()
  const { openOverlayInsteadOfNavigating } =
    useClientDashboardLoginOverlayTrigger()

  const CLIENT_DASHBOARD_LOGIN_TOKEN = '@clientDashboardLogin'

  const isClientDashboardLoginLink = useCallback(
    (url) => url === CLIENT_DASHBOARD_LOGIN_TOKEN,
    []
  )

  const handleButtonClick = useCallback(
    (href, name, isLeadCta) => {
      const data = {
        nav_link_section: 'Mega Menu',
        click_type: 'Button Click',
        click_id: name,
        click_url: href,
        click_text: `FDR-Web | ${name}`,
        track_event: 'mega_menu',
      }
      track(
        data,
        {
          variation: null,
          event_type: isLeadCta ? 'conversion' : 'track',
        },
        'click'
      )
    },
    [track]
  )

  const handleCtaClick = useCallback(
    (e, cta) => {
      const isDashboardLogin = isClientDashboardLoginLink(cta?.link)
      const clickName = isDashboardLogin
        ? 'Client Dashboard'
        : cta?.label || 'CTA'
      handleButtonClick(
        e.currentTarget.href || cta?.link,
        clickName,
        !isDashboardLogin && cta?.linkType === 'MainCTA'
      )
      if (isDashboardLogin) {
        openOverlayInsteadOfNavigating(e)
      }
    },
    [
      handleButtonClick,
      isClientDashboardLoginLink,
      openOverlayInsteadOfNavigating,
    ]
  )

  const handleOpenSidebar = () => {
    setOpenSidebar(!openSidebar)
  }

  useEffect(() => {
    if (hoverNavigation || hoverSubnav) {
      setOpenSubnav(true)
    } else {
      setOpenSubnav(false)
    }
  }, [hoverNavigation, hoverSubnav])

  useEffect(() => {
    if (isScrolling) {
      setHoverNavigation(false)
      setHoverSubnav(false)
      setOpenSubnav(false)
      setHoveredNavId(null)
    }
  }, [isScrolling])

  return (
    <ActiveNavProvider value={activeNav}>
      <div
        className={classNames('hidden min-h-25', {
          'block!': lastScrollDirectionZero === 'top' && isBelowStickyThreshold,
        })}
      ></div>
      <header
        data-testid="mega-menu"
        className={classNames('z-100 bg-white', {
          'fixed inset-x-0 top-0 z-50':
            lastScrollDirectionZero === 'top' && isBelowStickyThreshold,
          'shadow-[0_2px_2px_-2px_rgba(0,0,0,0.25)]': isBelowStickyThreshold,
        })}
      >
        <div
          className={
            'w-content max-w-360 mx-auto flex h-[70px] flex-col justify-center gap-2 bg-white px-2 py-1 lg:h-25 lg:px-8 lg:pt-2 lg:pb-0'
          }
        >
          <div className="hidden lg:block">
            {aboveHeaderCtas.map((cta) => (
              <div
                key={cta.label}
                className="flex w-full flex-row justify-end gap-2"
              >
                <div className="text-13">{cta.label}</div>
                <div className="text-13">
                  <a
                    href={cta.link}
                    rel="noopener"
                    target="_blank"
                    className="font-bold! cursor-pointer text-blue-585"
                  >
                    {cta.linkText}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="flex w-full flex-row gap-4 lg:justify-between">
            <div className="flex flex-col items-center justify-center lg:hidden">
              <button
                onClick={handleOpenSidebar}
                className="border-none bg-transparent"
              >
                <svg className="size-6 fill-current" viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                  />
                </svg>
              </button>
            </div>
            <div className="flex w-[85px] items-center justify-center lg:w-[130px] lg:-translate-y-2">
              <Logo />
            </div>
            <div className="hidden w-full justify-center lg:flex lg:flex-col">
              <div
                className="flex w-full cursor-pointer flex-row items-center justify-center gap-2 xl:gap-[clamp(1rem,2vw,2rem)]"
                id="mega-menu-navigation"
                onMouseLeave={() => {
                  setHoverNavigation(false)
                  setHoveredNavId(null)
                }}
              >
                {navigation?.map((item) => {
                  const hasChildren = item?.children?.length > 0
                  const isLink = !hasChildren && Boolean(item?.link)
                  const handleMouseEnter = () => {
                    setHoveredNavId(item.id)
                    if (hasChildren) {
                      setHoverNavigation(true)
                      setDataSubnav(item)
                      setHoverSubnav(true)
                    } else {
                      setHoverNavigation(false)
                      setHoverSubnav(false)
                    }
                  }

                  const isCurrentPage = activeNav.isActiveBranch(item)
                  // Keep the trigger highlighted while its own subnav stays open,
                  // even once the pointer has moved down into the panel.
                  const isHovered =
                    hoveredNavId === item.id ||
                    (openSubnav && dataSubnav?.id === item.id)
                  const isHighlighted = isHovered || isCurrentPage

                  if (isLink) {
                    return (
                      <NextLink
                        key={item.label}
                        href={item.link}
                        aria-current={isCurrentPage ? 'page' : undefined}
                        onMouseEnter={handleMouseEnter}
                      >
                        <NavItemLabel
                          label={item.label}
                          isHighlighted={isHighlighted}
                          isCurrentPage={isCurrentPage}
                        />
                      </NextLink>
                    )
                  }

                  return (
                    <div key={item.label} onMouseEnter={handleMouseEnter}>
                      <NavItemLabel
                        label={item.label}
                        isHighlighted={isHighlighted}
                        isCurrentPage={isCurrentPage}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="flex w-full flex-col items-end justify-center gap-2 lg:w-auto">
              <div className="hidden flex-row items-center justify-end gap-2 lg:flex">
                {topCtas
                  .filter((cta) => cta.show === 'web')
                  .map((cta) => {
                    const isLoginCta = isClientDashboardLoginLink(cta.link)

                    return (
                      <a
                        key={cta.label}
                        href={isLoginCta ? '#' : cta.link}
                        rel={isLoginCta ? undefined : 'noopener'}
                        target={isLoginCta ? undefined : '_blank'}
                        onClick={(e) => handleCtaClick(e, cta)}
                        className={classNames(
                          `whitespace-nowrap rounded-lg px-4 py-1 text-12 font-bold`,
                          {
                            'border-2 border-solid border-blue-base bg-blue-base text-white':
                              cta.style === 'primary',
                            'border-2 border-solid border-blue-135 bg-blue-135 text-blue-base':
                              cta.style === 'secondary',
                            'border-2 border-solid border-blue-base bg-white text-blue-base':
                              cta.style === 'outline',
                          }
                        )}
                      >
                        {cta.label}
                      </a>
                    )
                  })}
              </div>
              <div className="flex flex-row items-center justify-end gap-2 lg:hidden">
                {topCtas
                  .filter((cta) => cta.show === 'mobile')
                  .map((cta) => {
                    const isLoginCta = isClientDashboardLoginLink(cta.link)

                    return (
                      <a
                        key={cta.label}
                        href={isLoginCta ? '#' : cta.link}
                        rel={isLoginCta ? undefined : 'noopener'}
                        target={isLoginCta ? undefined : '_blank'}
                        onClick={(e) => handleCtaClick(e, cta)}
                        className={classNames(
                          `whitespace-nowrap rounded-lg px-4 py-1 text-12 font-bold`,
                          {
                            'border-2 border-solid border-blue-base bg-blue-base text-white':
                              cta.style === 'primary',
                            'border-2 border-solid border-blue-135 bg-blue-135 text-blue-base':
                              cta.style === 'secondary',
                            'border-2 border-solid border-blue-base bg-white text-blue-base':
                              cta.style === 'outline',
                          }
                        )}
                      >
                        {cta.label}
                      </a>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>
        <StickyRender
          logo={<Logo />}
          linkUrl={mainCTA?.link}
          lastScrollDirection={lastScrollDirection}
          isBelowStickyThreshold={isBelowStickyThreshold}
          showSticky={!ctasAreInViewport && scrollPosition > 150}
        />
        {openSubnav && (
          <Subnav
            hoverSubnav={hoverSubnav}
            setHoverSubnav={setHoverSubnav}
            setHoverNavigation={setHoverNavigation}
            data={dataSubnav}
          />
        )}
        <Sidebar
          iconHeader={<Logo />}
          open={openSidebar}
          handleClose={handleOpenSidebar}
          bottomCtas={
            <SidebarButtons
              sidebarCtas={sidebarCtas}
              isClientDashboardLoginLink={isClientDashboardLoginLink}
              handleCtaClick={handleCtaClick}
            />
          }
          navigationItems={navigation}
        />
      </header>
    </ActiveNavProvider>
  )
}

export default Megamenu
